import { DeviceSettingsType } from '@signageos/common-types/dist/Device/Settings/DeviceSettingsType';
import IPolicyCheckAndSetProperties from '../Device/Policy/IPolicyCheckAndSetProperties';
import Responsibility from '../../Feature/Responsibility';
import ManagementCapability from '../../NativeDevice/Management/ManagementCapability';
import { CheckAndSetResult, createDevicePolicySaga } from '../Device/Policy/devicePolicySagas';
import Property from '../../Property/Property';
import { AssignedPlugins } from '../../Plugins/pluginsSaga';
import { BrowserRuntime } from '../../CustomScripts/Runtimes/browserRuntime';
import { call, cancel, spawn } from 'redux-saga/effects';
import { isEqual } from 'lodash';
import { buffers, channel, Task } from 'redux-saga';

// Constants for plugin policy configuration
const POLICY_PLUGIN_WAIT_TIMEOUT = 2000;
const POLICY_PLUGIN_RETRY_COUNT = 20;
const POLICY_PLUGIN_RETRY_DELAY = 200;

/**
 * Utility function to get assigned plugins from property storage
 */
function* getAssignedPlugins(propertyStorage: any): any {
	try {
		// Try property storage first
		const assignedPlugins: AssignedPlugins = yield propertyStorage.getValueOrDefault(Property.DEVICE_PLUGINS, {});

		// If property storage returns valid data, use it
		if (assignedPlugins && typeof assignedPlugins === 'object' && Object.keys(assignedPlugins).length > 0) {
			return assignedPlugins;
		}

		// Fallback: Direct localStorage access
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && key.includes('Device.Plugins')) {
				try {
					const directData = JSON.parse(localStorage.getItem(key) || '{}');
					if (directData && typeof directData === 'object' && Object.keys(directData).length > 0) {
						return directData;
					}
				} catch (parseError) {
					console.error('Error parsing localStorage data:', parseError);
				}
			}
		}

		return {};
	} catch (error) {
		return {};
	}
}

/**
 * Utility function to find plugin for policy by pluginUid
 */
function findPluginForPolicy(assignedPlugins: AssignedPlugins | null | undefined, pluginUid: string) {
	if (!assignedPlugins || typeof assignedPlugins !== 'object') {
		return null;
	}

	const pluginValues = Object.values(assignedPlugins);
	const foundPlugin = pluginValues.find((plugin) => plugin.pluginUid === pluginUid);

	return foundPlugin;
}

/**
 * Utility function to get plugin iframe window
 */
function getPluginIframeWindow(iframeId: string): { iframe: HTMLIFrameElement | null; iframeWindow: any } {
	const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
	const iframeWindow = iframe?.contentWindow as any;
	return { iframe, iframeWindow };
}

/**
 * Utility function to wait for sosPlugin to be available in iframe
 */
function* waitForSosPlugin(iframeWindow: any, pluginUid: string): any {
	let retries = POLICY_PLUGIN_RETRY_COUNT;

	while (retries > 0) {
		if (iframeWindow && iframeWindow.sosPlugin && typeof iframeWindow.sosPlugin.get === 'function') {
			return iframeWindow.sosPlugin;
		}

		// Wait before retrying
		yield new Promise((resolve) => setTimeout(resolve, POLICY_PLUGIN_RETRY_DELAY));
		retries--;
	}

	const errorMsg =
		`sosPlugin not available for plugin ${pluginUid}. Final state: ` +
		`iframeWindow=${!!iframeWindow}, ` +
		`sosPlugin=${!!(iframeWindow && iframeWindow.sosPlugin)}, ` +
		`get=${!!(iframeWindow && iframeWindow.sosPlugin && iframeWindow.sosPlugin.get)}`;
	console.error(errorMsg);
	throw new Error(errorMsg);
}

/**
 * Utility function to create and run plugin iframe
 */
function* createAndRunPluginForPolicy(
	browserRuntime: BrowserRuntime,
	managementDriver: any,
	pluginForPolicy: AssignedPlugins[string],
): any {
	if (!pluginForPolicy) {
		throw new Error('Plugin for policy is null or undefined');
	}

	const messageChannel = channel<MessageEvent>(buffers.dropping(100));
	const messagesHandler = (event: MessageEvent) => {
		messageChannel.put(event);
	};

	yield call(
		browserRuntime.runPluginInBrowser,
		managementDriver,
		pluginForPolicy,
		pluginForPolicy.configuration || {},
		pluginForPolicy.pluginFile,
		pluginForPolicy.appletJsFile,
		messagesHandler,
	);

	const iframeId = pluginForPolicy.md5Checksum;
	const forMessages = yield spawn(browserRuntime.bindCustomScriptsMessages, messageChannel, window, iframeId);

	yield new Promise((resolve) => setTimeout(resolve, POLICY_PLUGIN_WAIT_TIMEOUT));

	return { messageChannel, forMessages, messagesHandler };
}

export const devicePluginPolicySaga = createDevicePolicySaga(
	DeviceSettingsType.PLUGIN,
	Responsibility.PLUGINS,
	ManagementCapability.PLUGINS,
	checkAndSetPluginSettings,
);

export async function* checkAndSetPluginSettings(
	properties: IPolicyCheckAndSetProperties<DeviceSettingsType.PLUGIN>,
): AsyncGenerator<any, CheckAndSetResult> {
	let createdIframe = false;
	let iframe: HTMLIFrameElement | null = null;
	let forMessages: Task | null = null;

	try {
		const pluginSettings = properties.policy.value;
		const assignedPlugins = yield* getAssignedPlugins(properties.propertyStorage);
		const pluginForPolicy = findPluginForPolicy(assignedPlugins, pluginSettings.pluginUid);

		if (!pluginForPolicy) {
			return CheckAndSetResult.NOT_CHANGED;
		}

		let iframeData = getPluginIframeWindow(pluginForPolicy.md5Checksum!);
		iframe = iframeData.iframe;
		let iframeWindow = iframeData.iframeWindow;

		if (!iframe || !iframeWindow) {
			const browserRuntime = new BrowserRuntime();
			const result = yield* createAndRunPluginForPolicy(browserRuntime, properties.managementDriver, pluginForPolicy);
			forMessages = result.forMessages;
			createdIframe = true;

			const pluginData = getPluginIframeWindow(pluginForPolicy.md5Checksum!);
			iframe = pluginData.iframe;
			iframeWindow = pluginData.iframeWindow;

			if (!iframe || !iframeWindow) {
				return CheckAndSetResult.NOT_CHANGED;
			}
		}

		const sosPlugin = yield* waitForSosPlugin(iframeWindow, pluginSettings.pluginUid);

		if (!sosPlugin || typeof sosPlugin.get !== 'function') {
			console.error('sosPlugin is invalid or missing get method:', sosPlugin);
			return CheckAndSetResult.NOT_CHANGED;
		}

		let currentPluginSettings;
		try {
			yield new Promise((resolve) => setTimeout(resolve, 500));
			currentPluginSettings = await sosPlugin.get();
		} catch (error) {
			currentPluginSettings = undefined;
		}

		if (!isEqual(pluginSettings.set, currentPluginSettings)) {
			yield call(sosPlugin.set, pluginSettings.set);
			return CheckAndSetResult.CHANGED;
		}

		return CheckAndSetResult.ALREADY_APPLIED;
	} catch (error) {
		return CheckAndSetResult.NOT_CHANGED;
	} finally {
		if (createdIframe && iframe) {
			if (forMessages) {
				yield cancel(forMessages);
			}
			if (iframe.parentNode) {
				iframe.parentNode.removeChild(iframe);
			}
		}
	}
}
